import React, { useEffect, useRef } from "react";

import * as d3 from "d3";

import applyBrush from "src/organization/pages/project/environment/metrics/components/Chart/brush";

import { useHttpMetricsContext } from "../context";
import { LayoutType } from "../types";

import Activities from "./activities";
import Ruler from "./ruler";
import * as S from "./style";
import Yaxis from "./Yaxis";

const DEFAULT_LEFT_TICKS = 6;

export type SvgLayoutProps = {
  showBrush?: boolean;
  onMouseMove?: (event) => void;
  onMouseLeave?: (event) => void;
} & LayoutType;

const SvgLayout = (props: SvgLayoutProps) => {
  const {
    positions,
    children,
    xScale,
    yScale,
    chartId,
    tick,
    onMouseMove,
    onMouseLeave,
    resourceName
  } = props;

  const svgRef = useRef<SVGSVGElement>(null);
  const xAxisRef = useRef<SVGGElement>(null);
  const context = useHttpMetricsContext();

  useEffect(() => {
    const axisBottom = d3
      .axisBottom(xScale)
      .ticks(tick?.x?.count)
      .tickFormat(tick?.x?.format);

    const axisGroup = d3
      .select(xAxisRef.current)
      .attr("transform", positions.xTransform)
      //@ts-expect-error d3 7.8.5 incorect type
      .call(axisBottom);

    axisGroup.selectAll(".tick").each(function (d) {
      //Note: the use of function literal is intentional here
      // so that d3 can bind the data to this keyword
      d3.select(this)
        .select("text")
        .classed("new-day-start", new Date(d as number).getHours() === 0);
    });
  }, [
    positions.xRangeEnd,
    positions.xRangeStart,
    positions.xTransform,
    tick?.x?.count,
    tick?.x?.format,
    xScale
  ]);

  const onBrushStart = event => {
    onMouseLeave?.(event);
  };

  const onBrushMove = event => {
    event;
  };

  const onBrushEnd = (x1, x2) => {
    context?.setTimeFrame({ from: x1, to: x2, zoomIn: true });
  };

  useEffect(() => {
    d3.select(svgRef.current)
      .select("svg [data-role=brush-container]")
      .call(
        //@ts-expect-error d3 7.8.5 incorect type
        applyBrush(xScale, yScale, onBrushEnd, onBrushStart, onBrushMove)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xScale, yScale]);

  const maxPoint = yScale.domain()[1];

  return (
    <S.Svg
      ref={svgRef}
      id={chartId}
      className="chart-graph"
      width={positions.width}
      height={positions.height}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      // onMouseUp tempoprary solution to when deployment overlay
      // modal is triggered. See getCursorDataFunc to use classList that excludes chart-graph
      // for better implementation. This has a bug on mobile when clicking on an activity
      onMouseUp={onMouseLeave}
    >
      <Ruler
        id={chartId}
        position={positions}
        ticksCount={tick?.y?.count || DEFAULT_LEFT_TICKS}
        xScale={xScale}
        yScale={yScale}
        max={maxPoint}
      />
      <S.Axis ref={xAxisRef} isXAxis />

      {children}

      {(props.showBrush ?? true) && (
        <S.Brush data-role="brush-container" transform={positions.yTransform} />
      )}
      <Activities yScale={yScale} xScale={xScale} resourceName={resourceName} />
      <Yaxis
        numProgression={tick?.y?.count || DEFAULT_LEFT_TICKS}
        xAxis={xScale}
        yAxis={yScale}
        maxPoint={maxPoint}
        transform={positions.yTransform}
        format={tick?.y?.format}
      />
    </S.Svg>
  );
};

export default SvgLayout;
